import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ViewEncapsulation,
} from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { coerceBooleanProperty } from '@angular/cdk/coercion';

@Component({
  selector: 'bll-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'leumi-bll-select',
    '[class.leumi-bll-select-inline]': 'inline',
    role: 'button',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => BllSelectComponent), multi: true }],
})
export class BllSelectComponent implements ControlValueAccessor {
  @Input() items: any;
  @Input() loading: boolean;
  @Input() bindLabel: string | null = null;
  @Input() bindValue: string | null = null;
  @Input() selected: any = null;
  @Input() isEnglish: boolean = false;
  @Input() errorMsg: string | null = null;
  @Input() placeholder: string | null = null;
  @Input() hideIcon: boolean;
  @Input()
  get inline(): any {
    return this._dInline;
  }
  set inline(value: any) {
    this._dInline = coerceBooleanProperty(value);
  }
  private _dInline: boolean;

  @Input()
  get disabled(): any {
    return this._disabled;
  }
  set disabled(value: any) {
    const newDisabledState = coerceBooleanProperty(value);
    if (this._disabled !== newDisabledState) {
      this._disabled = newDisabledState;
      this._changeDetectorRef.detectChanges();
    }
  }
  protected _disabled: boolean = false;

  @Input()
  get hidePlaceholder(): any {
    return this._hidePlaceholder;
  }
  set hidePlaceholder(value: any) {
    const newDisabledState = coerceBooleanProperty(value);
    if (this._hidePlaceholder !== newDisabledState) {
      this._hidePlaceholder = newDisabledState;
      this._changeDetectorRef.detectChanges();
    }
  }
  protected _hidePlaceholder: boolean = false;

  @Input()
  get hideErrorMsg(): any {
    return this._hideErrorMsg;
  }
  set hideErrorMsg(value: any) {
    const newDisabledState = coerceBooleanProperty(value);
    if (this._hideErrorMsg !== newDisabledState) {
      this._hideErrorMsg = newDisabledState;
      this._changeDetectorRef.detectChanges();
    }
  }
  protected _hideErrorMsg: boolean = false;

  @Input()
  get searchable(): any {
    return this._searchable;
  }
  set searchable(value: any) {
    const newDisabledState = coerceBooleanProperty(value);
    if (this._searchable !== newDisabledState) {
      this._searchable = newDisabledState;
      this._changeDetectorRef.detectChanges();
    }
  }
  protected _searchable: boolean = false;

  @Input()
  get clearable(): any {
    return this._clearable;
  }
  set clearable(value: any) {
    const newDisabledState = coerceBooleanProperty(value);
    if (this._clearable !== newDisabledState) {
      this._clearable = newDisabledState;
      this._changeDetectorRef.detectChanges();
    }
  }
  protected _clearable: boolean = false;

  @Input()
  set args(args) {
    if (!args) {
      return;
    }
    this.argsLegacySupport(args);
  }

  @Output() change = new EventEmitter<any>();
  constructor(public _changeDetectorRef: ChangeDetectorRef) {}
  onChange(selectedVal: any) {
    this.setSelectedItem(selectedVal);
    this._controlValueAccessorChangeFn(this.selected);
    this.change.emit(this.selected);
  }

  isShowError() {
    return this.errorMsg && this.errorMsg !== '';
  }

  argsLegacySupport(params: any) {
    if (!params.list) {
      return;
    }
    this.items = params.list;
    this.bindLabel = params.key;
    this.bindValue = params.bindValue;
    this.errorMsg = params.errorMsg;
    this.hideErrorMsg = params.hideErrorMsg;
    this.placeholder = params.placeHolder;
    this.disabled = params.disabled;
    if (params.selectedItem) {
      const val = params.list.filter((v) => v[params.key] === params.selectedItem[params.key]);
      if (val) {
        this.setSelectedItem(val[0]);
      }
    }
    this.hidePlaceholder = params.hidePlaceHolder;
    this.isEnglish = params.isEnglish;
  }

  setSelectedItem(value: any) {
    const isObject = typeof value === 'object' && value !== undefined && value !== null;
    this.selected = this.bindValue && isObject ? value[this.bindValue] : value;
  }

  /**
   * Sets the model value. Implemented as part of ControlValueAccessor.
   */
  public writeValue(value: any) {
    this.setSelectedItem(value);
    this._changeDetectorRef.detectChanges();
  }

  /** The method to be called in order to update ngModel */
  _controlValueAccessorChangeFn: (value: any) => void = () => {};
  /**
   * Registers a callback to be triggered when the model value changes.
   * Implemented as part of ControlValueAccessor.
   * @param fn Callback to be registered.
   */
  registerOnChange(fn: (value: any) => void) {
    this._controlValueAccessorChangeFn = fn;
  }

  onTouched: () => any = () => {};
  /**
   * Registers a callback to be triggered when the control is touched.
   * Implemented as part of ControlValueAccessor.
   * @param fn Callback to be registered.
   */
  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  /**
   * Sets the disabled state of the control. Implemented as a part of ControlValueAccessor.
   * @param isDisabled Whether the control should be disabled.
   */
  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
  }
}
